import React, { memo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Tabs } from 'antd';
import { up } from 'styled-breakpoints';
import styled, { css } from 'styled-components';
import { match, P } from 'ts-pattern';
import { Contact } from '@lgg/isomorphic/types/__generated__/graphql';
import { ContactDetailsForm } from 'src/components/domain/contacts/contact-form/contact-details-form';
import {
  ContactModalOpenLegacyCallModalIcon,
  ContactModalTabs,
  ContactModalViewProps,
  ContactOptionsDisplayProps,
  MoreOptionsIcon,
  useContactOptions,
} from 'src/components/domain/contacts/contact-modal/contact-modal.shared';
import { ContactModalLoadingView } from 'src/components/domain/contacts/contact-modal/tabs/contact-modal-tabs.shared';
import { LggOptionsDropdownButtonWithCustomTrigger } from 'src/components/general/button/dropdown-button';
import { ButtonV2 } from 'src/components/general/button/lgg-button';
import { StyledCollapse, Panel } from 'src/components/general/display/collapse';
import { Scrollbar } from 'src/components/general/display/scrollbar';
import { Icon } from 'src/components/general/icon';
import { LggModal } from 'src/components/general/modals/lgg-modal';
import { FlexColumn } from 'src/components/layout/flex-column';
import { FlexRow } from 'src/components/layout/flex-row';
import {
  BlockContactModal,
  useContactBlock,
} from 'src/components/pages/contacts/components/contact-block';
import { ContactAddNewDropdownWithTitle } from 'src/components/pages/conversations/components/contact-interactions/contact-interactions-list-header';
import { useFormatLastInteractionDate } from 'src/components/pages/conversations/hooks/use-format-last-interaction-date';
import { useAuthorization } from 'src/hooks/use-authorization';
import { useShowInlineForms } from 'src/hooks/use-show-inline-forms';
import { useVisible } from 'src/hooks/use-visible';
import { ContactAttachments } from '../contact-details/contact-attachments/contact-attachments';
import { ContactDetails } from '../contact-details/contact-details';
import { ContactDetailsFormWrapper } from '../contact-details/shared';
import { useEditContactModal } from '../hooks/use-edit-contact-modal';

const ContactOptionsDropdown = memo<
  ContactOptionsDisplayProps & { onBlockContactOptionClicked: VoidFunction }
>(({ visibilityHandler, contact, onBlockContactOptionClicked }) => {
  const contactOptions = useContactOptions({
    contact,
    onBlockContactOptionClicked,
  });

  if (!contactOptions) {
    return null;
  }

  return (
    <LggOptionsDropdownButtonWithCustomTrigger
      visibilityHandler={visibilityHandler}
      customTrigger={
        <MoreOptionsIcon
          data-lgg-id="contact-options-dropdown-trigger"
          type="moreOptionsWithPadding"
        />
      }
      options={contactOptions}
    />
  );
});

const StyledModal = styled(LggModal)`
  height: calc(100% - 60px);
  margin: 0 auto;
  max-width: 1400px;
  padding-bottom: 0;

  .ant-modal-content {
    height: 100%;
    max-width: unset;
    width: 100%;
  }

  .ant-modal-close {
    display: none;
  }

  .ant-modal-body {
    height: 100%;
  }
`;

const ModalContent = styled(FlexColumn)`
  height: 100%;
`;

const ModalTitle = styled.span`
  color: ${(props) => props.theme.colors.carbon};
  font-family: ${(props) => props.theme.font.regular};
  font-size: 24px;
  font-stretch: normal;
  font-style: normal;
  font-weight: normal;
  letter-spacing: -0.48px;
  line-height: 30px;
  text-align: left;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
  min-width: 0;
`;

const ModalSubTitle = styled.span`
  align-items: center;
  color: ${(props) => props.theme.colors.flint};
  display: flex;
  font-family: ${(props) => props.theme.font.regular};
  font-size: 14px;
  font-stretch: normal;
  font-style: normal;
  font-weight: normal;
  letter-spacing: normal;
  line-height: 17px;
  margin-top: 1px;
  text-align: left;
`;

const ModalHeader = styled(FlexRow)`
  align-items: center;
  background-color: ${(props) => props.theme.colors.white};
  height: 90px;
  min-height: 90px;
  padding: 0 20px;
  width: 100%;
`;

const LabelColumn = styled(FlexColumn)`
  flex: 1;
  min-width: 0;
`;

const ButtonsRow = styled(FlexRow)`
  align-items: center;

  > * {
    margin-left: 10px;
  }
`;

const ModalBody = styled(FlexRow)`
  background-color: ${({ theme }) => theme.colors.porcelain};
  flex: 1;
  overflow: hidden;
  padding: 20px;
`;

const ContactDetailsContainer = styled.div<{ $variant: 'regular' | 'redesigned' }>`
  background-color: ${({ theme }) => theme.colors.white};
  border-radius: 6px;
  margin-left: 19px;
  min-width: 206px;
  width: 206px;

  ${up('lg')} {
    ${({ $variant }) =>
      $variant === 'redesigned' &&
      css`
        min-width: 320px;
        width: 320px;
      `}
    ${({ $variant }) =>
      $variant === 'regular' &&
      css`
        min-width: 304px;
        width: 304px;
      `}
  }
`;

const CloseIcon = styled(Icon)`
  align-self: center;

  svg {
    height: 18px;
    width: 18px;

    path {
      fill: ${({ theme }) => theme.colors.flint};
    }
  }

  ${up('md')} {
    && {
      margin-left: 20px;
    }
  }
`;

const EditContactButton = styled(ButtonV2)`
  margin-left: 15px;
`;

const FullContactDetailsHeaderBackIcon = styled(Icon)`
  cursor: pointer;
  svg {
    height: 12px;
    width: 12px;
  }
`;

const FullContactDetailsHeader = styled(FlexRow)`
  align-items: center;
  padding: 12px 20px;
  border-bottom: 1px solid var(--Light-Greys-Porcelain-2, #edf0f2);
  border-top: 1px solid var(--Light-Greys-Porcelain-2, #edf0f2);
  background: rgba(249, 250, 251, 0.8);
  gap: 10px;
  margin-bottom: 20px;
`;

const FullContactDetailsHeaderTitle = styled.span`
  color: #57728a;
  font-family: ${({ theme }) => theme.font.medium};
  font-size: 13px;
  line-height: 16px;
  letter-spacing: -0.26px;
`;

export const ContactFullDetailsContent = ({
  contact,
  onBack,
}: {
  contact: Contact;
  onBack: () => void;
}) => {
  const { t } = useTranslation(['contacts']);
  return (
    <>
      <FullContactDetailsHeader>
        <FullContactDetailsHeaderBackIcon
          type="arrowLeftWide"
          onClick={onBack}
          lggTestId="contact-full-details-back"
        />
        <FullContactDetailsHeaderTitle>
          {t('contacts:information.title')}
        </FullContactDetailsHeaderTitle>
      </FullContactDetailsHeader>
      <ContactDetailsForm key={contact.id} contact={contact} variant="light" />
    </>
  );
};

type DesktopViewProps = ContactModalViewProps;

export const ContactModalDesktopView = memo<DesktopViewProps>(
  ({ contact, onClose, loading }) => {
    const { formatLastInteractionDate } = useFormatLastInteractionDate();
    const { showEditContactModal } = useEditContactModal();
    const { getFeatureFlag } = useAuthorization();
    const { t } = useTranslation(['common', 'contacts']);
    const hasCrmFeatureFlag = getFeatureFlag('crm');
    const moreOptionsDrawerVisibility = useVisible();
    const { changeBlockStatus, blockedIndicator } = useContactBlock({ contact });
    const blockContactModalVisibility = useVisible();
    const isAttachmentsSectionActive = getFeatureFlag('contact-attachments');
    const showInlineContactForm = useShowInlineForms();
    const [contactDetailsTab, setContactDetailsTab] = useState<'brief' | 'full'>('brief');

    return (
      <StyledModal
        data-lgg-id="contact-modal"
        visible={true}
        closeIcon={null}
        onClose={onClose}
        width="calc(100% - 60px)"
        maskTransitionName=""
      >
        <ModalContent>
          {match({ contact, loading })
            .with({ contact: P.nonNullable }, ({ contact }) => {
              const occurredAt = contact.lastContactInteraction?.occurredAt;

              return (
                <>
                  <ModalHeader>
                    <LabelColumn>
                      <ModalTitle data-lgg-id="contact-modal-title">
                        {contact.label}
                      </ModalTitle>
                      <ModalSubTitle>
                        {blockedIndicator && (
                          <>
                            {blockedIndicator}
                            {' · '}
                          </>
                        )}
                        {occurredAt
                          ? formatLastInteractionDate(occurredAt)
                          : t('common:noInteractionLogged')}
                      </ModalSubTitle>
                    </LabelColumn>
                    <ButtonsRow>
                      <ContactModalOpenLegacyCallModalIcon
                        contact={contact}
                        data-lgg-id="contact-interaction-list-header-phone-call"
                      />
                      {hasCrmFeatureFlag && (
                        <ContactAddNewDropdownWithTitle contactId={contact.id} />
                      )}
                      <EditContactButton
                        icon="edit"
                        size="regular"
                        variant="default"
                        onClick={() => showEditContactModal(contact.id)}
                      >
                        {t('contacts:actions.editContact')}
                      </EditContactButton>
                      <ContactOptionsDropdown
                        visibilityHandler={moreOptionsDrawerVisibility}
                        contact={contact}
                        onBlockContactOptionClicked={() =>
                          changeBlockStatus(blockContactModalVisibility)
                        }
                      />
                      <BlockContactModal
                        visibilityHandler={blockContactModalVisibility}
                        contact={contact}
                      />
                      <CloseIcon
                        onClick={onClose}
                        type="close"
                        lggTestId="contact-modal-close-icon"
                      />
                    </ButtonsRow>
                  </ModalHeader>
                  <ModalBody>
                    <ContactModalTabs contact={contact} />
                    <ContactDetailsContainer
                      $variant={showInlineContactForm ? 'redesigned' : 'regular'}
                    >
                      <Scrollbar>
                        <Tabs
                          defaultActiveKey={contactDetailsTab}
                          activeKey={contactDetailsTab}
                          animated={true}
                          renderTabBar={() => <></>}
                        >
                          <Tabs.TabPane key="brief">
                            <StyledCollapse
                              ghost
                              defaultActiveKey={['1', '2']}
                              className="desktop-collapse"
                            >
                              <Panel header={t('contacts:information.title')} key="1">
                                {showInlineContactForm ? (
                                  <ContactDetailsFormWrapper>
                                    <ContactDetailsForm
                                      key={contact.id}
                                      contact={contact}
                                      variant="light"
                                      onViewAll={() => {
                                        setContactDetailsTab('full');
                                      }}
                                    />
                                  </ContactDetailsFormWrapper>
                                ) : (
                                  <ContactDetails contact={contact} />
                                )}
                              </Panel>
                              {isAttachmentsSectionActive && (
                                <Panel header={t('contacts:attachments.title')} key="2">
                                  <ContactAttachments contactId={contact.id} />
                                </Panel>
                              )}
                            </StyledCollapse>
                          </Tabs.TabPane>
                          <Tabs.TabPane key="full">
                            <ContactFullDetailsContent
                              contact={contact}
                              onBack={() => setContactDetailsTab('brief')}
                            />
                          </Tabs.TabPane>
                        </Tabs>
                      </Scrollbar>
                    </ContactDetailsContainer>
                  </ModalBody>
                </>
              );
            })
            .with({ loading: true }, () => <ContactModalLoadingView />)
            .otherwise(() => null)}
        </ModalContent>
      </StyledModal>
    );
  },
);
